import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  IDLE_WARN_MS,
  HEARTBEAT_MS,
  clearAuth,
  hasSession,
  heartbeat,
  markActivity,
  msUntilIdleLogout,
} from '../utils/session';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Panel = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem 1.6rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.3);
  text-align: center;

  h2 {
    margin: 0 0 0.4rem;
    font-size: 1.1rem;
    color: #0f172a;
  }
  p {
    margin: 0 0 1rem;
    font-size: 0.88rem;
    color: #64748b;
    line-height: 1.5;
  }
  .count {
    font-size: 2.4rem;
    font-weight: 700;
    color: #dc2626;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    margin-bottom: 1rem;
  }
  .row { display: flex; gap: 0.5rem; justify-content: center; flex-wrap: wrap; }
  button {
    font-family: inherit;
    font-size: 0.88rem;
    font-weight: 600;
    border-radius: 10px;
    padding: 0.6rem 1.1rem;
    min-height: 44px;
    cursor: pointer;
  }
  .primary { background: #2563eb; border: 1px solid #2563eb; color: #fff; }
  .ghost { background: #fff; border: 1px solid #e2e8f0; color: #334155; }
`;

// Routes where signing someone out would be pointless or hostile.
const PUBLIC_PATHS = ['/', '/login'];

/**
 * Ends an idle session and keeps the cross-tab heartbeat alive.
 *
 * Activity is recorded in localStorage rather than component state so that
 * working in one tab keeps every other tab alive too — otherwise a background
 * tab would quietly time out and take the shared token with it.
 */
const SessionGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [msLeft, setMsLeft] = useState(null);
  const signedOutRef = useRef(false);

  const onPublicPage = PUBLIC_PATHS.includes(location.pathname);

  const signOut = useCallback(() => {
    if (signedOutRef.current) return;
    signedOutRef.current = true;
    clearAuth();
    setMsLeft(null);
    navigate('/', { replace: true });
  }, [navigate]);

  const stayIn = useCallback(() => {
    markActivity();
    setMsLeft(null);
  }, []);

  // Any real interaction counts. Passive listeners so this can never make
  // scrolling feel heavy on a tablet.
  useEffect(() => {
    if (onPublicPage) return undefined;
    signedOutRef.current = false;
    const events = ['mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'];
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last < 2000) return;   // throttle: this writes to localStorage
      last = now;
      markActivity();
    };
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, onActivity));
  }, [onPublicPage]);

  // Heartbeat: tells other tabs (and the next cold start) that a window is up.
  useEffect(() => {
    heartbeat();
    const id = setInterval(heartbeat, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, []);

  // The countdown itself.
  useEffect(() => {
    if (onPublicPage) { setMsLeft(null); return undefined; }
    const tick = () => {
      if (!hasSession()) { setMsLeft(null); return; }
      const left = msUntilIdleLogout();
      if (left <= 0) { signOut(); return; }
      setMsLeft(left <= IDLE_WARN_MS ? left : null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [onPublicPage, signOut]);

  if (msLeft === null) return null;

  const seconds = Math.max(0, Math.ceil(msLeft / 1000));
  return (
    <Backdrop role="alertdialog" aria-modal="true" aria-labelledby="idle-title">
      <Panel>
        <h2 id="idle-title">Still there?</h2>
        <p>You’ve been inactive. This session will sign out automatically to protect the terminal.</p>
        <div className="count" aria-live="assertive">{seconds}s</div>
        <div className="row">
          <button type="button" className="primary" onClick={stayIn} autoFocus>
            Stay signed in
          </button>
          <button type="button" className="ghost" onClick={signOut}>
            Sign out now
          </button>
        </div>
      </Panel>
    </Backdrop>
  );
};

export default SessionGuard;
